import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, FileText, Home } from "lucide-react";
import { toast } from "sonner";

export default function PaymentResult() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [txId, setTxId] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const MAX = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setTxId(data.tx_id);
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        toast.error(formatApiError(e));
        setStatus("failed");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4" data-testid="payment-result-page">
      <div className="bg-white border border-slate-200 rounded-lg p-10 max-w-md w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 className="h-14 w-14 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold">Verifica pagamento in corso…</h1>
            <p className="text-sm text-slate-500 mt-2">Attendere qualche istante mentre confermiamo l&apos;operazione con il gateway.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold text-emerald-700">Pagamento completato</h1>
            <p className="text-sm text-slate-600 mt-2">Grazie! L&apos;operazione è andata a buon fine. La tua agenzia è stata avvisata e riceverai la ricevuta via email.</p>
            <div className="flex gap-2 mt-6 justify-center">
              <button
                data-testid="goto-receipt"
                onClick={() => navigate(`/area-riservata/ricevuta/${txId}`)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
              >
                <FileText className="h-4 w-4" /> Apri ricevuta
              </button>
              <button
                onClick={() => navigate("/area-riservata")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm"
              >
                <Home className="h-4 w-4" /> Area riservata
              </button>
            </div>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="h-14 w-14 text-amber-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold text-amber-700">Sessione scaduta</h1>
            <p className="text-sm text-slate-600 mt-2">La sessione di pagamento è scaduta. Puoi ritentare quando preferisci.</p>
            <button onClick={() => navigate("/area-riservata")} className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0F172A] hover:bg-[#1E293B] text-white text-sm font-semibold">
              Torna all&apos;area riservata
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold text-red-700">Pagamento non riuscito</h1>
            <p className="text-sm text-slate-600 mt-2">Non siamo riusciti a confermare l&apos;operazione. Riprova oppure scegli un altro metodo.</p>
            <button onClick={() => navigate("/area-riservata")} className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0F172A] hover:bg-[#1E293B] text-white text-sm font-semibold">
              Torna all&apos;area riservata
            </button>
          </>
        )}
      </div>
    </div>
  );
}
